import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { EMPTY_FUNCTION } from '../../constants/common'
import { ScreenContainerRef } from '../../context'
import {
  useDate,
  useInitData,
  useLinkLikeClick,
  usePageBrowserTabTitle,
  useUserData,
} from '../../hooks'
import { getUserFullName, getUserId } from '../../utils/user'
import { Page } from '../Page'

import { BirdviewAnalytics } from './BirdviewAnalytics'
import { BirdviewControl } from './BirdviewControl'
import { BirdviewDashboardView } from './BirdviewDashboardView'
import { BirdviewHeader } from './BirdviewHeader'
import { BirdviewScope, BirdviewView } from './DashboardBirdview.constants'
import { QA_NAME_DASHBOARD_BIRDVIEW } from './DashboardBirdview.constants.qa'
import {
  BirdviewEffectiveScopeContext,
  BirdviewHeaderActionsContainerContext,
} from './DashboardBirdview.contexts'
import {
  useBirdviewAnalyticsLoaders,
  useBirdviewChartControls,
  useBirdviewLayoutReset,
  useBirdviewSectionLoaders,
  useDashboardBirdviewQuickActions,
  useDashboardBirdviewSections,
} from './DashboardBirdview.hooks'
import {
  checkIsBirdviewAnalyticsAvailable,
  checkIsBirdviewAnalyticsView,
  checkIsBirdviewOversightSkeleton,
  getBirdviewAnalyticsPreset,
  getBirdviewEffectiveScope,
  getBirdviewEffectiveView,
  getBirdviewRole,
} from './DashboardBirdview.utils'
import { checkIsBirdviewSectionsLoading } from './DashboardBirdview.utils.sections'
import { getBirdviewStorageScope } from './DashboardBirdview.utils.storage'

import * as styles from './DashboardBirdview.module.css'

function DashboardBirdview() {
  const { t } = useTranslation()

  usePageBrowserTabTitle({ pageTitle: t('BrowserTab.DashboardPageTitleKey') })

  const user = useUserData()

  const { availableSections, sections: sectionsPermissions } = useInitData()

  const [view, setView] = useState(BirdviewView.Dashboard)
  const [scope, setScope] = useState(BirdviewScope.Mine)
  const [headerActionsContainer, setHeaderActionsContainer] = useState(null)

  const dashboardRef = useRef(null)

  const date = useDate()

  const { handleClick } = useLinkLikeClick()

  const userId = getUserId(user)
  const role = getBirdviewRole(user.role)
  const effectiveScope = getBirdviewEffectiveScope({ role, scope })
  const isAnalyticsAvailable = checkIsBirdviewAnalyticsAvailable({
    availableSections,
    role,
    sectionsPermissions,
  })
  const effectiveView = getBirdviewEffectiveView({ isAnalyticsAvailable, view })
  const isAnalyticsView = checkIsBirdviewAnalyticsView(effectiveView)
  const storageScope = getBirdviewStorageScope({ role: role.value, userId })
  const preset = getBirdviewAnalyticsPreset(role)

  const { getters: dashboardGetters } = useBirdviewSectionLoaders(role)
  const { getters: analyticsGetters } = useBirdviewAnalyticsLoaders()

  const {
    activityDoneById,
    handleActivityToggle,
    handleResetQuickActions,
    modal,
    pendingIds,
    requestActions,
  } = useDashboardBirdviewQuickActions()

  const { chartControls, handleChangeChartControls, handleResetChartControls } =
    useBirdviewChartControls({ storageScope })

  const { handleResetLayout, layoutKey } = useBirdviewLayoutReset({
    isAnalyticsView,
    storageScope,
    onReset: isAnalyticsView ? handleResetChartControls : EMPTY_FUNCTION,
  })

  // One sections store serves both views, so switching back finds them loaded.
  const { isFirstLoad, reload, sections } = useDashboardBirdviewSections({
    getters: isAnalyticsView ? analyticsGetters : dashboardGetters,
    params: { preset, scope: effectiveScope, userId },
    sectionParams: chartControls,
    onReload: handleResetQuickActions,
  })

  // Profiles the NG app has no page for yet resolve to old-webapp links, which
  // the router cannot serve: the link-like handler opens those in place.
  function handleNavigate(link) {
    handleClick({ link })
  }

  return (
    <Page qaName={QA_NAME_DASHBOARD_BIRDVIEW}>
      <ScreenContainerRef value={dashboardRef}>
        <BirdviewHeaderActionsContainerContext value={headerActionsContainer}>
          <BirdviewEffectiveScopeContext value={effectiveScope}>
            <div className={styles.dashboard} ref={dashboardRef}>
              <BirdviewHeader
                ScopeControl={
                  checkIsBirdviewOversightSkeleton(role)
                    ? BirdviewControl
                    : null
                }
                ViewControl={isAnalyticsAvailable ? BirdviewControl : null}
                actionsContainerRef={setHeaderActionsContainer}
                date={date}
                greetingName={getUserFullName(user)}
                isLoading={isFirstLoad}
                isRefreshing={checkIsBirdviewSectionsLoading(sections)}
                scope={effectiveScope}
                view={effectiveView}
                onChangeScope={setScope}
                onChangeView={setView}
                onRefresh={reload}
                onResetLayout={isAnalyticsView ? null : handleResetLayout}
              />

              {isAnalyticsView ? (
                <BirdviewAnalytics
                  chartControls={chartControls}
                  key={layoutKey}
                  preset={preset}
                  sections={sections}
                  storageScope={storageScope}
                  onChangeChartControls={handleChangeChartControls}
                  onNavigate={handleNavigate}
                  onResetLayout={handleResetLayout}
                />
              ) : (
                <BirdviewDashboardView
                  activityDoneById={activityDoneById}
                  key={layoutKey}
                  pendingIds={pendingIds}
                  requestActions={requestActions}
                  role={role}
                  scope={effectiveScope}
                  sections={sections}
                  storageScope={storageScope}
                  onActivityToggle={handleActivityToggle}
                  onNavigate={handleNavigate}
                />
              )}
            </div>
          </BirdviewEffectiveScopeContext>
        </BirdviewHeaderActionsContainerContext>
      </ScreenContainerRef>
      {modal}
    </Page>
  )
}

export default DashboardBirdview
